import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App';
import LoadingSpinner from './components/LoadingSpinner';
import { InstallPromptProvider } from "./context/InstallPromptContext";
import { AuthProvider } from "./context/AuthContext";

// #region agent log
const _dbg = (loc, msg, data, h) => { try { fetch('http://127.0.0.1:7242/ingest/65706569-8702-4a74-8eda-c60d62e0ef50',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:loc,message:msg,data:data||{},hypothesisId:h,timestamp:Date.now(),sessionId:'debug-session'})}).catch(()=>{}); } catch(e){} };
// #endregion

// Global wheel scroll fix: Video and some elements block wheel→scroll.
// Manually scroll the document when the default scroll would not occur.
(function() {
  const handleWheel = (e) => {
    // If target is inside a scrollable container that can scroll, let it handle
    let el = e.target;
    while (el && el !== document.body) {
      const s = getComputedStyle(el);
      const oy = s.overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) {
        const up = el.scrollTop > 0;
        const down = el.scrollTop < el.scrollHeight - el.clientHeight;
        if ((e.deltaY < 0 && up) || (e.deltaY > 0 && down)) return;
      }
      el = el.parentElement;
    }
    window.scrollBy(1, e.deltaY);
    e.preventDefault();
  };
  document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
})();

// #region agent log
(function debugScroll() {
  const logScrollState = () => {
    const body = document.body;
    const html = document.documentElement;
    const root = document.getElementById('root');
    const bodyStyle = body ? getComputedStyle(body) : null;
    const htmlStyle = html ? getComputedStyle(html) : null;
    _dbg('index.js:logScrollState', 'Scroll state snapshot', {
      bodyOverflow: bodyStyle?.overflow,
      bodyOverflowY: bodyStyle?.overflowY,
      bodyPosition: bodyStyle?.position,
      bodyScrollHeight: body?.scrollHeight,
      bodyClientHeight: body?.clientHeight,
      htmlOverflow: htmlStyle?.overflow,
      htmlOverflowY: htmlStyle?.overflowY,
      bodyClassList: Array.from(body?.classList || []),
      htmlClassList: Array.from(html?.classList || []),
      scrollingElement: document.scrollingElement?.tagName,
      rootOverflow: root ? getComputedStyle(root).overflow : null,
      rootOverflowY: root ? getComputedStyle(root).overflowY : null,
      innerHeight: window.innerHeight,
      scrollY: window.scrollY
    }, 'A');
  };
  let wheelCount = 0;
  const onWheelCapture = (e) => {
    wheelCount++;
    if (wheelCount <= 3) {
      _dbg('index.js:onWheelCapture', 'Wheel capture (before other handlers)', {
        defaultPrevented: e.defaultPrevented,
        targetTag: e.target?.tagName,
        deltaY: e.deltaY
      }, 'B');
    }
  };
  const onWheelBubble = (e) => {
    if (wheelCount <= 3) {
      _dbg('index.js:onWheelBubble', 'Wheel bubble (after other handlers)', {
        defaultPrevented: e.defaultPrevented,
        targetTag: e.target?.tagName
      }, 'B');
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { logScrollState(); });
  } else {
    logScrollState();
  }
  setTimeout(logScrollState, 2000);
  window.addEventListener('wheel', onWheelCapture, { passive: true, capture: true });
  window.addEventListener('wheel', onWheelBubble, { passive: true, capture: false });
})();
// #endregion

// Show loading spinner while React loads
const rootElement = document.getElementById('root');
if (rootElement) {
  ReactDOM.render(<LoadingSpinner />, rootElement);
}

// Load the main app
ReactDOM.render(
  <React.StrictMode>
    <InstallPromptProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </InstallPromptProvider>
  </React.StrictMode>,
  rootElement
);

// Register Service Worker for PWA with Update Notification
if ('serviceWorker' in navigator) {
  let swRegistration = null;
  
  // Only register in production or if explicitly enabled
  const isProduction = process.env.NODE_ENV === 'production';
  const enableSW = isProduction || window.location.hostname !== 'localhost';
  
  if (enableSW) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js', { scope: '/' })
        .then((registration) => {
          console.log('SW registered: ', registration);
          swRegistration = registration;
          
          // Check for updates every 5 minutes
          setInterval(() => {
            registration.update().catch((err) => {
              console.log('SW update check failed:', err);
            });
          }, 300000);
          
          // Listen for service worker updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New service worker available - show update notification
                  // Get version from service worker script if possible
                  fetch('/service-worker.js')
                    .then(res => res.text())
                    .then(text => {
                      const versionMatch = text.match(/CACHE_VERSION\s*=\s*['"]([^'"]+)['"]/);
                      const version = versionMatch ? versionMatch[1] : 'unknown';
                      showUpdateNotification('A new version is available. Click "Update" to refresh.', version);
                    })
                    .catch(() => {
                      showUpdateNotification('A new version is available. Click "Update" to refresh.', 'unknown');
                    });
                }
              });
            }
          });
          
          // Listen for messages from service worker
          navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'SW_UPDATE_AVAILABLE') {
              const version = event.data.version || 'unknown';
              showUpdateNotification(event.data.message || 'A new version is available. Click "Update" to refresh.', version);
            }
          });
          
          // Check if there's a waiting service worker on initial load
          // Use a small delay to ensure DOM is ready
          setTimeout(() => {
          if (registration.waiting) {
              fetch('/service-worker.js?' + Date.now())
                .then(res => res.text())
                .then(text => {
                  const versionMatch = text.match(/CACHE_VERSION\s*=\s*['"]([^'"]+)['"]/);
                  const version = versionMatch ? versionMatch[1] : 'unknown';
                  showUpdateNotification('A new version is available. Click "Update" to refresh.', version);
                })
                .catch(() => {
                  showUpdateNotification('A new version is available. Click "Update" to refresh.', 'unknown');
                });
          }
          }, 1000);
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
          // Don't show error to user in development
        });
    });
  } else {
    // In development, unregister any existing service workers
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister();
      });
    });
  }
  
  // Check for updates when page becomes visible
  if (enableSW) {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && 'serviceWorker' in navigator && swRegistration) {
        swRegistration.update().catch((err) => {
          console.log('SW update on visibility change failed:', err);
        });
      }
    });
  }
  
  // Show update notification modal
  function showUpdateNotification(message = 'A new version is available. Click "Update" to refresh.', version = 'unknown') {
    // Only show update notification in standalone mode (PWA installed)
    const isStandalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      (window.matchMedia('(display-mode: fullscreen)').matches && 
       !window.navigator.userAgent.includes('Chrome'));
    
    // If not in standalone mode, don't show the notification
    if (!isStandalone) {
      console.log('Update notification skipped: Not in standalone mode');
      return;
    }
    
    // Check if notification already shown on this page
    if (document.getElementById('pwa-update-notification')) return;
    
    // For version tracking: only prevent showing if user explicitly dismissed this version
    // We'll track dismissed versions separately from shown versions
    const dismissedVersionsKey = 'pwa-update-dismissed-versions';
    const dismissedVersions = JSON.parse(localStorage.getItem(dismissedVersionsKey) || '[]');
    
    // If user clicked "Later" for this version, don't show again until next version
    if (dismissedVersions.includes(version)) {
      return;
    }
    
    const notification = document.createElement('div');
    notification.id = 'pwa-update-notification';
    notification.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      border-radius: 16px;
      padding: 2rem;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      z-index: 10002;
      text-align: center;
      animation: modalSlideIn 0.3s ease-out;
    `;
    
    notification.innerHTML = `
      <div style="margin-bottom: 1.5rem;">
        <div style="
          width: 64px;
          height: 64px;
          margin: 0 auto 1rem;
          background: rgba(139, 92, 246, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
        </div>
        <h3 style="font-size: 1.5rem; font-weight: 600; color: #1F3A93; margin: 0 0 0.5rem 0;">
          Update Available
        </h3>
        <p style="font-size: 1rem; color: #666; margin: 0; line-height: 1.6;">
          ${message}
        </p>
      </div>
      <div style="display: flex; gap: 0.75rem;">
        <button id="pwa-update-later" style="
          flex: 1;
          padding: 0.75rem 1.5rem;
          background: transparent;
          color: #666;
          border: 2px solid #ddd;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          font-size: 1rem;
          min-height: 48px;
        ">Later</button>
        <button id="pwa-update-now" style="
          flex: 1;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%, #06b6d4 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          font-size: 1rem;
          min-height: 48px;
        ">Update</button>
      </div>
    `;
    
    // Add backdrop
    const backdrop = document.createElement('div');
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.6);
      z-index: 10001;
      animation: fadeIn 0.2s ease-out;
    `;
    
    document.body.appendChild(backdrop);
    document.body.appendChild(notification);
    
    // Handle update button
    document.getElementById('pwa-update-now').addEventListener('click', () => {
      if (swRegistration && swRegistration.waiting) {
        // Tell the waiting service worker to skip waiting and activate
        swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
        // Reload the page
        window.location.reload();
      } else {
        // Force reload to get new version
        window.location.reload();
      }
    });
    
    // Handle later button
    document.getElementById('pwa-update-later').addEventListener('click', () => {
      // Mark this version as dismissed so it won't show again until next version
      const dismissedVersionsKey = 'pwa-update-dismissed-versions';
      const dismissedVersions = JSON.parse(localStorage.getItem(dismissedVersionsKey) || '[]');
      if (!dismissedVersions.includes(version)) {
        dismissedVersions.push(version);
        localStorage.setItem(dismissedVersionsKey, JSON.stringify(dismissedVersions));
      }
      
      if (document.body.contains(backdrop)) {
      document.body.removeChild(backdrop);
      }
      if (document.body.contains(notification)) {
      document.body.removeChild(notification);
      }
    });
    
    // Close on backdrop click
    backdrop.addEventListener('click', () => {
      if (document.body.contains(backdrop)) {
      document.body.removeChild(backdrop);
      }
      if (document.body.contains(notification)) {
      document.body.removeChild(notification);
      }
    });
  }
}

